import React, { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';

import SelfieCamera from '../components/SelfieCamera.jsx';
import face from './assets/face.json';
import { getTextDefaults as getFaceTextDefaults } from './translations/FaceSectionTranslation.js';
import { getTextDefaults as getBiometricTextDefaults } from './translations/BiometricSelfieSectionTranslation.js';

const COMPARE_URL = '/api/check/biometric-compare';

/**
 * Biometric selfie verification section for kiosk check-in (9ID-883).
 *
 * Mirrors TestSection's in-session model: attempts are looped *inside* the section and
 * nothing is submitted while tries remain, so the task stays `todo` during retries. Only
 * a terminal outcome submits — match -> done, exhausted/no-reference -> the value carries
 * `isFailed: true` so the task is submitted as `failed` (-> user in review).
 */
const BiometricSelfieSection = ({
	navigationProps,
	onChange = () => {},
	name = 'biometricVerification',
	maxAttempts = 3,
	textOverride = {},
	cameraIncludes,
	addPendingAction = () => {},
	removePendingAction = () => {},
	selectedLanguage = 'EN',
	disabled = false,
	style = {},
}) => {
	const text = Object.assign({}, getBiometricTextDefaults(selectedLanguage), textOverride);
	const [stage, setStage] = useState('welcome'); // 'welcome' | 'camera' | 'result'
	const [outcome, setOutcome] = useState(null); // { type: 'match' | 'retry' | 'fail', reason?, attempt? }
	const [attempts, setAttempts] = useState(0);

	const complete = (value, nextOutcome) => {
		// Persist the outcome into the form value so the kiosk submit derives the task status
		// (isFailed -> failed, else done), consistent with TestSection / the other task UIs.
		onChange({ target: { name, value: { ...value, attempts: attempts + (nextOutcome.type === 'match' ? 0 : 1) } } });
		setOutcome(nextOutcome);
		setStage('result');
	};

	// SelfieCamera posts the selfie to the compare endpoint and hands us back the verdict.
	const handleVerdict = useCallback(
		(evt) => {
			const status = evt?.target?.value?.status;

			if (status === 'match') {
				complete({ isFailed: false, status: 'match' }, { type: 'match' });
				return;
			}
			if (status === 'no-reference') {
				// No reference exists — retrying cannot help, so fail immediately.
				complete({ isFailed: true, status: 'no-reference' }, { type: 'fail', reason: 'no-reference' });
				return;
			}

			// mismatch, no-face, or a transient error: retry until attempts run out
			const usedAttempts = attempts + 1;
			setAttempts(usedAttempts);
			if (usedAttempts >= maxAttempts) {
				onChange({ target: { name, value: { isFailed: true, status: 'mismatch', attempts: usedAttempts } } });
				setOutcome({ type: 'fail', reason: 'mismatch' });
				setStage('result');
			} else {
				// stay in-section, do NOT submit — task remains `todo`
				setOutcome({ type: 'retry', attempt: usedAttempts });
				setStage('result');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[attempts, maxAttempts, name, onChange],
	);

	// Auto-advance on a successful match (submits -> done).
	useEffect(() => {
		if (stage === 'result' && outcome?.type === 'match') {
			const timer = setTimeout(() => navigationProps.next({ preventDefault: () => {} }), 1400);
			return () => clearTimeout(timer);
		}
	}, [stage, outcome, navigationProps]);

	const goToCamera = () => setStage(disabled ? 'welcome' : 'camera');
	const retry = () => {
		setOutcome(null);
		setStage('camera');
	};
	const finish = () => navigationProps.next({ preventDefault: () => {} });

	if (stage === 'welcome') {
		return (
			<SectionContainer style={style}>
				<WelcomeScreen animation={face}>
					<Typography variant="h6">{text.welcomeTitle}</Typography>
					<Typography>{text.welcomeReason}</Typography>
				</WelcomeScreen>
				<SectionNav
					{...navigationProps}
					next={goToCamera}
					navigation={{ nextLabel: text.getStarted }}
					hasPrevious={false}
					hasNext={true}
					inline
				/>
			</SectionContainer>
		);
	}

	if (stage === 'camera') {
		return (
			<SectionContainer style={{ ...style, justifyContent: 'center' }}>
				<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
					<SelfieCamera
						cameraIncludes={cameraIncludes}
						onBackPress={() => setStage('welcome')}
						onComplete={handleVerdict}
						uploadUrl={COMPARE_URL}
						name={name}
						addPendingAction={addPendingAction}
						removePendingAction={removePendingAction}
						text={getFaceTextDefaults(selectedLanguage)}
						getTextDefaults={getFaceTextDefaults}
					/>
				</Box>
			</SectionContainer>
		);
	}

	// Result
	const isMatch = outcome?.type === 'match';
	const isRetry = outcome?.type === 'retry';
	const attemptLabel = text.attemptCounter
		.replace('{current}', String(outcome?.attempt ?? attempts))
		.replace('{max}', String(maxAttempts));

	return (
		<SectionContainer
			style={style}
			Navigation={
				isRetry ? (
					// tries remain: only offer "Try again" — do NOT allow proceeding (task stays todo)
					<SectionNav
						{...navigationProps}
						next={retry}
						navigation={{ nextLabel: text.tryAgain }}
						hasPrevious={false}
						hasNext={true}
						inline
					/>
				) : !isMatch ? (
					// terminal failure: proceeding submits the value with isFailed -> task `failed`
					<SectionNav
						{...navigationProps}
						next={finish}
						navigation={{ ...navigationProps.navigation, nextLabel: text.finish }}
						hasPrevious={false}
						hasNext={true}
						inline
					/>
				) : null
			}
		>
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 4, gap: 2 }}>
				{isMatch && <Alert severity="success">{text.verified}</Alert>}
				{isRetry && (
					<>
						<Alert severity="error">{text.tryAgainTitle}</Alert>
						<Typography>{text.tryAgainBody}</Typography>
						<Typography variant="body2" color="text.secondary">
							{attemptLabel}
						</Typography>
					</>
				)}
				{outcome?.type === 'fail' && (
					<Alert severity="error">
						{outcome.reason === 'no-reference' ? text.noReference : text.mismatchFinal}
					</Alert>
				)}
			</Box>
		</SectionContainer>
	);
};

BiometricSelfieSection.type = 'BiometricSelfieSection';
BiometricSelfieSection.hasNavigation = true;

export default BiometricSelfieSection;
